import { getCurrentUser } from "@/apis/user/route";
import { CurrentUserCompany } from "@/apis/user/types";
import { identifyClarityUser } from "@/lib/clarity";
import { auth } from "@/lib/firebase";
import { isAdminEmail } from "@/lib/internal-email";
import * as Sentry from "@sentry/react";
import {
  completeMfaSignIn,
  getMfaHints,
  signInWithPassword,
  signOutUser,
} from "@/services/auth-service";
import {
  MultiFactorInfo,
  MultiFactorResolver,
  onAuthStateChanged,
  RecaptchaVerifier,
} from "firebase/auth";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export interface AuthUser {
  email: string | null;
  id: string;
  displayName: string | null;
  photoURL: string | null;
  authenticatedAt: number;
  isAdmin: boolean;
  company: CurrentUserCompany | null;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  authError: string | null;
  mfaRequired: boolean;
  mfaResolver: MultiFactorResolver | null;
  mfaVerificationId: string | null;
  mfaSelectedHint: MultiFactorInfo | null;
  isFirstLogin: boolean;
  signInWithPasswordAuth: (
    email: string,
    password: string,
  ) => Promise<{
    success: boolean;
    requiresMfa?: boolean;
    error?: string;
  }>;
  getMfaPhoneNumber: () => Promise<string>;
  verifyMfaCode: (code: string) => Promise<boolean>;
  logout: () => Promise<{ success: boolean; error?: string }>;
  isAuthenticated: boolean;
  getMfaHints: () => MultiFactorInfo[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(
    null,
  );
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(
    null,
  );
  const [mfaSelectedHint, setMfaSelectedHint] =
    useState<MultiFactorInfo | null>(null);
  const [recaptchaVerifier, setRecaptchaVerifier] =
    useState<RecaptchaVerifier | null>(null);

  const [isFirstLogin, setIsFirstLogin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {

        // 회사 정보는 서버의 current-user 응답에서 가져온다.
        let company: CurrentUserCompany | null = null;
        try {
          const userResponse = await getCurrentUser();
          if (userResponse.status === 200) {
            company = userResponse.data?.company ?? null;
          }
        } catch {
          // current-user 조회 실패 시 회사 정보 없이 진행
        }

        const authUser: AuthUser = {
          email: firebaseUser.email,
          id: firebaseUser.uid,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          authenticatedAt: Date.now(),
          isAdmin: isAdminEmail(firebaseUser.email),
          company,
        };
        setUser(authUser);
        // Clarity 세션을 실제 유저와 연결 — 대시보드에 이메일 표시 (production 외에는 no-op)
        identifyClarityUser(authUser.id, authUser.email);
        // Sentry 이벤트에 유저 식별 부착 — Clarity 누락 시 유저별 활동 교차 확인용
        Sentry.setUser({
          id: authUser.id,
          email: authUser.email ?? undefined,
        });
        // Clear MFA state when user is authenticated
        setMfaRequired(false);
        setMfaResolver(null);
        setMfaVerificationId(null);
        setMfaSelectedHint(null);
        // Clear RecaptchaVerifier
        if (recaptchaVerifier) {
          recaptchaVerifier.clear();
          setRecaptchaVerifier(null);
        }
      } else {
        setUser(null);
        Sentry.setUser(null);
      }
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [recaptchaVerifier]);

  useEffect(() => {
    if (recaptchaVerifier) {
      recaptchaVerifier.clear();
    }
  }, []);

  // useEffect(() => {
  //   if (!user) return;
  //   const checkFirstLogin = async () => {
  //     try {
  //       const userResponse = await getCurrentUser();
  //       if (userResponse.status === 200) {
  //         setIsFirstLogin(false);
  //       } else if (userResponse.status === 404) {
  //         setIsFirstLogin(true);
  //       }
  //     } catch (error) {}
  //   };
  //   checkFirstLogin();
  // }, [user]);

  const signInWithPasswordAuth = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{
      success: boolean;
      requiresMfa?: boolean;
      error?: string;
    }> => {
      setIsLoading(true);
      setAuthError(null);

      try {
        const result = await signInWithPassword(email, password);

        if (result.requiresMfa) {
          // Set MFA state without changing loading state
          setMfaResolver(result.resolver || null);
          setMfaRequired(true);
          // Don't set isLoading to false here to avoid flash
          return { success: false, requiresMfa: true };
        }

        if (!result.success && result.error) {
          setAuthError(result.error);
          setIsLoading(false);
          return { success: false, error: result.error };
        }

        setIsLoading(false);
        return { success: true };
      } catch (error) {
        setIsLoading(false);
        const errorMessage =
          error instanceof Error ? error.message : "Sign in failed";
        setAuthError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [],
  );

  const getMfaPhoneNumber = useCallback(async (): Promise<string> => {
    if (!mfaResolver) {
      throw new Error("MFA resolver not available");
    }

    try {
      // Create or reuse RecaptchaVerifier
      let verifier = recaptchaVerifier;
      if (!verifier) {
        verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
          callback: () => {
            // reCAPTCHA solved silently
          },
          "expired-callback": () => {
            // reCAPTCHA expired, clear it
            if (recaptchaVerifier) {
              recaptchaVerifier.clear();
              setRecaptchaVerifier(null);
            }
          },
        });
        // Render the verifier to initialize it
        await verifier.render();
        setRecaptchaVerifier(verifier);
      }

      // const result = await startMfaVerification(mfaResolver, verifier);
      // Store verification ID and selected hint in context state
      // setMfaVerificationId(result.verificationId);
      // setMfaSelectedHint(result.selectedHint);
      return "";
    } catch (error) {
      throw error;
    }
  }, [mfaResolver, recaptchaVerifier]);

  const verifyMfaCode = useCallback(
    async (code: string): Promise<boolean> => {
      if (!mfaResolver || !mfaVerificationId) {
        throw new Error(
          "Verification ID not found. Please request a new code.",
        );
      }

      try {
        await completeMfaSignIn(mfaResolver, mfaVerificationId, code);

        // Clear MFA state on success
        setMfaRequired(false);
        setMfaResolver(null);
        setMfaVerificationId(null);
        setMfaSelectedHint(null);

        // Clear RecaptchaVerifier
        if (recaptchaVerifier) {
          setRecaptchaVerifier(null);
        }

        return true;
      } catch (error) {
        return false;
      }
    },
    [mfaResolver, mfaVerificationId, recaptchaVerifier],
  );

  const logout = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    setIsLoading(true);

    try {
      await signOutUser();
      setUser(null);
      setMfaRequired(false);
      setMfaResolver(null);
      setMfaVerificationId(null);
      setMfaSelectedHint(null);
      setAuthError(null);
      // Clear RecaptchaVerifier
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      }
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      setIsLoading(false);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Sign out failed",
      };
    }
  }, [recaptchaVerifier]);

  const value: AuthContextType = {
    user,
    isLoading,
    authError,
    mfaRequired,
    mfaResolver,
    mfaVerificationId,
    mfaSelectedHint,
    isFirstLogin,
    signInWithPasswordAuth,
    getMfaPhoneNumber,
    verifyMfaCode,
    logout,
    isAuthenticated: !!user,
    getMfaHints: () => getMfaHints(mfaResolver),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
